import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';
import type { CreateAnticipoInput, UpdateAnticipoInput } from '@shared/validation/anticipo-schemas';

// ── DTOs ──

export interface AnticipoDTO {
  id: string;
  proforma_id: string | null;
  project_id: string;
  monto: number;
  fecha_recepcion: string;
  comprobante_s3_key: string | null;
  notas: string | null;
  created_by: { id: string; nombre: string };
  created_at: string;
  updated_at: string;
}

// ── List ──
export function useAnticipos(projectId: string) {
  return useQuery({
    queryKey: queryKeys.anticipos.list(projectId),
    queryFn: async () => {
      const response = await api.get<{ data: AnticipoDTO[] }>(`/projects/${projectId}/anticipos`);
      return response.data.data;
    },
    enabled: !!projectId,
  });
}

// ── Create ──
export function useCreateAnticipo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, data }: { projectId: string; data: CreateAnticipoInput }) => {
      const response = await api.post<{ data: { id: string; monto: number; total_pagos: number } }>(
        `/projects/${projectId}/anticipos`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.anticipos.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.summary });
      toast('success', `Pago de $${_data.monto.toFixed(2)} registrado`);
    },
  });
}

// ── Update (admin+) ──
export function useUpdateAnticipo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ anticipoId, projectId: _projectId, data }: { anticipoId: string; projectId: string; data: UpdateAnticipoInput }) => {
      const response = await api.put<{ data: { id: string; monto: number } }>(
        `/anticipos/${anticipoId}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.anticipos.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.summary });
      toast('success', 'Pago actualizado');
    },
  });
}

// ── Delete (admin+) ──
export function useDeleteAnticipo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ anticipoId, projectId: _projectId }: { anticipoId: string; projectId: string }) => {
      const response = await api.delete<{ data: { success: boolean } }>(`/anticipos/${anticipoId}`);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.anticipos.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.summary });
      toast('success', 'Pago eliminado');
    },
  });
}
